import { config } from 'lib-shared-backend-config';
import type { MetaConversation as MetaConversationT } from 'lib-shared-legacy-types';
import orderBy from 'lodash/orderBy';
import _sortBy from 'lodash/sortBy';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { AppDispatch } from 'bootstrap.store';
import { TypingType } from 'components/conversation/reducers/typing';
import type { UserPresence } from 'components/presence/reducers/presence';
import { userModalActions } from 'components/usersModal';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import * as conversationsActions from '../../conversation/actions/conversations';
import * as typingActions from '../../conversation/actions/typing';
import * as personalActions from '../../settings/actions/personal';
import { hasFeature } from '../../shared/featureFlag';
import { SidebarMenu } from '../components/sidebarMenu';
import type {
  DispatchPropsT,
  LeftMenuTogglePayloadT,
  OwnPropsT,
  StatePropsT,
} from '../components/sidebarMenu';

const { conversationsRemove } = conversationsActions.actions;
const { typingRemove } = typingActions.actions;
const { personalLeftMenu } = personalActions.actions;

const menuDevNotices: StatePropsT['menuDevNotices'] = [];

if (config.backendDomain) {
  menuDevNotices.push({
    title: 'BD',
    label: `Backend Domain: ${config.backendDomain}`,
    onClose: () => {
      sessionStorage.removeItem('dixa:backend-domain');
      window.location.reload();
    },
  });
}

const statusMap = { working: 0, away: 1, offline: 2 };
const getPresence = (state: RootReducerStateT): RootReducerStateT['presence'] =>
  state.presence;
const getPresenceByStatus = createSelector([getPresence], (presence) => {
  const sortedAllPresence = _sortBy(presence.allPresence, [
    function (element) {
      const rank = {
        working: 1,
        acw: 2,
        away: 3,
        offline: 4,
        unknown: 5,
      };
      return rank[element.presence.status];
    },
    function (element) {
      return element.user.name !== undefined && element.user.name !== null
        ? element.user.name.toLowerCase()
        : '';
    },
  ]);
  return sortedAllPresence.reduce(
    (pre: UserPresence[][], userPresence: UserPresence) => {
      const {
        presence: { status },
      } = userPresence;
      if (statusMap[status] !== undefined && pre[statusMap[status]]) {
        pre[statusMap[status]].push(userPresence);
      }
      return pre;
    },
    [[], [], []],
  );
});

const getMetaConversations = (state: RootReducerStateT) =>
  state.conversations.metaConversations;
const sortMetaConversations = (item: MetaConversationT) => {
  if (item.conversation.status === 'open') {
    return 1;
  }
  if (['awaitingpending', 'pending'].includes(item.conversation.status || '')) {
    return 2;
  }
  if (item.conversation.status === 'closed') {
    return 3;
  }

  return 4;
};

const getMetaConversationsSorted = (state: RootReducerStateT) => {
  return orderBy(
    getMetaConversations(state),
    [sortMetaConversations, 'isAssignedToMe', 'conversation.id'],
    ['asc', 'desc', 'desc'],
  );
};

const BREAK_POINT_FOR_HIDING = 5;
const getMetaConversationsBeforeAndAfterBreak = createSelector(
  [
    getMetaConversationsSorted,
    (state: RootReducerStateT) =>
      state.presence.presence.presence_status.load_descriptions.filter(
        (load_description) => load_description.load_type === 'acw',
      ),
  ],
  (metaConversations, loadDescriptions) => {
    const before: MetaConversationT[] = [];
    const after: MetaConversationT[] = [];
    metaConversations.forEach((metaConversation, index) => {
      if (
        metaConversation.conversation.status === 'closed' &&
        loadDescriptions.some(
          (loadDescription) =>
            metaConversation.conversation.id === loadDescription.csid,
        )
      ) {
        before.unshift(metaConversation);
      } else if (
        index < BREAK_POINT_FOR_HIDING ||
        metaConversation.conversation.status === 'open'
      ) {
        before.push(metaConversation);
      } else {
        after.push(metaConversation);
      }
    });

    return { before, after };
  },
);

const mapStateToProps = (state: RootReducerStateT): StatePropsT => {
  const { before, after } = getMetaConversationsBeforeAndAfterBreak(state);

  return {
    profile: state.settings.profile.profile,
    presence: state.presence,
    presenceByStatus: getPresenceByStatus(state),
    searchParams: state.search.params,
    metaConversation: state.conversation.metaConversation,
    metaConversations: state.conversations.metaConversations,
    metaConversationsBeforeBreak: before,
    metaConversationsAfterBreak: after,
    typings: state.typing.typings,
    conversationsLastSearchParams: state.conversationsPage.lastSearchParams,
    isMenuOpen: state.settings.personal.isLeftMenuOpen,
    hasChatWithDixaSupport: hasFeature(state, 'has-chat-with-dixa-support')
      ? !!state.widget.presence?.show_widget
      : false,
    menuDevNotices,
    hasKnowledgeEnabled: hasFeature(state, 'knowledge-access'),
    hasAnalytics: hasFeature(state, 'custom-analytics'),
    hasQA: hasFeature(state, 'quality-assurance'),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchPropsT => {
  return {
    showUserModal: () =>
      dispatch(userModalActions.show({ user: { type: 'member' } })),
    typingRemove: (typing: TypingType) => dispatch(typingRemove(typing)),
    conversationsRemove: (csid: number) => dispatch(conversationsRemove(csid)),
    wsRequest: bindRtkActionToPromise(dispatch, websocketActions.request),
    personalLeftMenuToggle: bindActionToPromise<
      LeftMenuTogglePayloadT,
      unknown,
      unknown
    >(dispatch, personalLeftMenu),
  };
};

export const Menu = connect<
  StatePropsT,
  DispatchPropsT,
  OwnPropsT,
  RootReducerStateT
>(
  mapStateToProps,
  mapDispatchToProps,
)(SidebarMenu);
